import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const EventTypes = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  
  const [loading, setLoading] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    slug: '',
    duration_minutes: 30,
    connection_methods: [],
    buffer_time_before: 0,
    buffer_time_after: 0,
    color: '#667eea'
  });

useEffect(() => {
  fetchEventTypes();
  fetchAgentSlug();
}, []);

useEffect(() => {
  if (autoGenerateSlug && formData.title && !editingId) {
    const autoSlug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    
    // Use setFormData directly to avoid infinite loop
    setFormData(prev => ({ ...prev, slug: autoSlug }));
  }
}, [formData.title, autoGenerateSlug, editingId]);



  const fetchEventTypes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/event-types/agent/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEventTypes(data.data);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch agent's public_slug from profile API
  const fetchAgentSlug = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/agents/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const agentData = data.data || data;
        if (agentData.public_slug) {
          // Update user in state and localStorage
          const updatedUser = { ...user, public_slug: agentData.public_slug };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }
    } catch (error) {
      console.error('Error fetching agent slug:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = editingId 
        ? `/api/v1/event-types/${editingId}`
        : '/api/v1/event-types';
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      

if (response.ok) {
  const data = await response.json();
  if (editingId) {
    alert('✅ Updated successfully!');
  } else {
    alert(`✅ Created successfully!\n\nLink: agents.matix.one/book/${user.public_slug || user.username}/${data.data.slug}`);
  }
  setShowForm(false);
        setEditingId(null);
        resetForm();
        fetchEventTypes();
      } else {
        const data = await response.json();
        alert('❌ ' + (data.message || 'An error occurred'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ An error occurred');
    }
  };

  const handleEdit = (eventType) => {
    setEditingId(eventType.id);
    setFormData({
      title: eventType.title,
      description: eventType.description || '',
      slug: eventType.slug,
      duration_minutes: eventType.duration_minutes,
      connection_methods: typeof eventType.connection_methods === 'string' 
        ? JSON.parse(eventType.connection_methods) 
        : eventType.connection_methods || [],
      buffer_time_before: eventType.buffer_time_before || 0,
      buffer_time_after: eventType.buffer_time_after || 0,
      color: eventType.color || '#667eea'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event type?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/event-types/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('✅ Deleted successfully!');
        fetchEventTypes();
      } else {
        alert('❌ Delete failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ An error occurred');
    }
  };

  const resetForm = () => {
        setAutoGenerateSlug(true);  // Reset auto-generate on form reset

    setFormData({
      title: '',
      description: '',
      slug: '',
      duration_minutes: 30,
      connection_methods: [],
      buffer_time_before: 0,
      buffer_time_after: 0,
      color: '#667eea'
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleConnectionMethod = (method) => {
    setFormData(prev => ({
      ...prev,
      connection_methods: prev.connection_methods.includes(method)
        ? prev.connection_methods.filter(m => m !== method)
        : [...prev.connection_methods, method]
    }));
  };

  const connectionMethodsLabels = {
    zoom: '🎥 Zoom Meeting',
    google_meet: '📹 Google Meet',
    phone: '📞 Phone Call',
    in_person: '👥 In-Person Visit'
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏰</div>
          <div>Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '25px',
          borderRadius: '12px',
          marginBottom: '25px',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px' }}>📅 Event Types</h1>
            <p style={{ margin: '5px 0 0 0', opacity: 0.9 }}>
              Create different event types that customers can book with you
            </p>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingId(null);
              resetForm();
            }}
            style={{
              padding: '12px 24px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}>
            {showForm ? '❌ Cancel' : '➕ Add New Type'}
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} style={{
            backgroundColor: 'rgba(26, 32, 56, 0.8)',
            padding: '25px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              {editingId ? '✏️ Edit Event Type' : '➕ Create New Event Type'}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>
                  Event Title *
                </label>
               
                <input
                  type="text"
                  value={formData.title}
onChange={(e) => {
  handleChange('title', e.target.value);
  if (!editingId) {
    setAutoGenerateSlug(true);
  }
}}                  required
                  placeholder="e.g. Technical Consultation"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    backgroundColor: '#1a2038',
                    color: '#f9fafb'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>
                  Event Link (Slug) *
                </label>
                <input
                  type="text"
                  value={formData.slug}
onChange={(e) => {
    setAutoGenerateSlug(false);
    handleChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
  }}
    onFocus={() => setAutoGenerateSlug(false)}

  required
placeholder="Auto-generated from title"
style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    backgroundColor: '#1a2038',
                    color: '#f9fafb'
                  }}
                />
                <small style={{ color: '#9ca3af', fontSize: '12px' }}>
                  Link: agents.matix.one/book/{user.public_slug || user.username}/{formData.slug}
                </small>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                placeholder="Brief description of this event type"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  backgroundColor: '#1a2038',
                  color: '#f9fafb',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>
                  Duration (min) *
                </label>
                <input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value))}
                  required
                  min="5"
                  step="5"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    backgroundColor: '#1a2038',
                    color: '#f9fafb'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>
                  Buffer Before (min)
                </label>
                <input
                  type="number"
                  value={formData.buffer_time_before}
                  onChange={(e) => handleChange('buffer_time_before', parseInt(e.target.value))}
                  min="0"
                  step="5"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    backgroundColor: '#1a2038',
                    color: '#f9fafb'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>
                  Buffer After (min)
                </label>
                <input
                  type="number"
                  value={formData.buffer_time_after}
                  onChange={(e) => handleChange('buffer_time_after', parseInt(e.target.value))}
                  min="0"
                  step="5"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    backgroundColor: '#1a2038',
                    color: '#f9fafb'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '12px', color: '#d1d5db' }}>
                Connection Methods * (select one or more)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {Object.entries(connectionMethodsLabels).map(([method, label]) => {
                  const isSelected = formData.connection_methods.includes(method);
                  return (
                    <button
                      key={method}
                      type="button"
                      onClick={() => toggleConnectionMethod(method)}
                      style={{
                        padding: '12px',
                        backgroundColor: isSelected ? '#667eea' : 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: `2px solid ${isSelected ? '#667eea' : 'rgba(255,255,255,0.2)'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                      <span>{isSelected ? '✅' : '⬜'}</span>
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>
                Color
              </label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => handleChange('color', e.target.value)}
                style={{
                  width: '80px',
                  height: '40px',
                  border: '2px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '700'
              }}>
              {editingId ? '💾 Save Changes' : '➕ Create Event Type'}
            </button>
          </form>
        )}

        {/* Event Types List */}
        {eventTypes.length === 0 ? (
          <div style={{
            backgroundColor: 'rgba(26, 32, 56, 0.8)',
            padding: '60px',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#9ca3af'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📅</div>
            <h3 style={{ color: '#f9fafb', marginBottom: '10px' }}>No Event Types</h3>
            <p>Start by creating a new event type so customers can book appointments with you</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {eventTypes.map(eventType => {
              const connectionMethods = typeof eventType.connection_methods === 'string' 
                ? JSON.parse(eventType.connection_methods) 
                : eventType.connection_methods || [];
              
              return (
                <div
                  key={eventType.id}
                  style={{
                    backgroundColor: 'rgba(26, 32, 56, 0.8)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderLeft: `4px solid ${eventType.color}`
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#f9fafb' }}>
                        {eventType.title}
                      </h3>
                      {eventType.description && (
                        <p style={{ margin: '0 0 12px 0', color: '#9ca3af', fontSize: '14px' }}>
                          {eventType.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', fontSize: '14px', color: '#d1d5db' }}>
                        <span>⏱️ {eventType.duration_minutes} min</span>
                        <span style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                          onClick={() => {
                            const link = 'https://agents.matix.one/book/' + (user.public_slug || user.username) + '/' + eventType.slug;
                            navigator.clipboard.writeText(link).then(() => alert('Link copied!'));
                          }}>
                          🔗 agents.matix.one/book/{user.public_slug || user.username}/{eventType.slug}
                          <span style={{ fontSize: '12px', background: 'rgba(102,126,234,0.3)', padding: '2px 8px', borderRadius: '6px' }}>📋 Copy</span>
                        </span>
                      </div>
                      <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {connectionMethods.map(method => (
                          <span
                            key={method}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: 'rgba(102, 126, 234, 0.2)',
                              color: '#a5b4fc',
                              borderRadius: '12px',
                              fontSize: '13px'
                            }}>
                            {connectionMethodsLabels[method]}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(eventType)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}>
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(eventType.id)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}>
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EventTypes;